import { useEffect, useRef, useState, useCallback } from "react";
import { api, API, useAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Users, Send, Paperclip, ArrowLeft, FileText, Download } from "lucide-react";

const ROLE_LABEL = { trainee: "Student", assessor: "Instructor", admin: "Program Admin", superadmin: "Super Admin", employer: "Employer" };
const timeStr = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const sizeStr = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function FileBubble({ m, mine }) {
  const url = `${API}/messages/attachments/${m.id}`;
  if (m.file_type?.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={`file-msg-${m.id}`}>
        <img src={url} alt={m.file_name} className="rounded-xl max-h-52 max-w-full border border-slate-200" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`file-msg-${m.id}`}
      className={`flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 border ${mine ? "bg-teal-800 border-teal-700 text-white" : "bg-white border-slate-200 text-slate-800"}`}>
      <FileText size={20} className={mine ? "text-teal-200" : "text-teal-700"} />
      <span className="min-w-0">
        <span className="block text-sm font-semibold truncate max-w-[200px]">{m.file_name}</span>
        <span className={`block text-[10px] ${mine ? "text-teal-200" : "text-slate-400"}`}>{m.file_size ? sizeStr(m.file_size) : "File"} · tap to open</span>
      </span>
      <Download size={15} className={mine ? "text-teal-200" : "text-slate-400"} />
    </a>
  );
}

function Room({ trackId, onBack }) {
  const { user } = useAuth();
  const { pick } = useLang();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const load = useCallback(() => api.get(`/tracks/${trackId}/chat`).then((r) => setData(r.data)).catch(() => {}), [trackId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/tracks/${trackId}/chat`, { text });
      setText("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  const sendFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/tracks/${trackId}/chat/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="text-slate-500 p-6">Loading…</p>;
  return (
    <div className="flex flex-col h-full" data-testid="group-chat-room">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <button data-testid="group-back-btn" onClick={onBack} className="lg:hidden text-slate-400 hover:text-slate-600"><ArrowLeft size={18} /></button>
        <span className="w-9 h-9 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Users size={17} /></span>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 text-sm truncate">{pick(data.track.title)}</p>
          <p className="text-[11px] text-slate-400">Class chat — everyone enrolled can see these messages</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/50">
        {data.messages.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No messages yet — start the class discussion!</p>}
        {data.messages.map((m) => {
          const mine = m.sender_id === user.id;
          const staff = ["assessor", "admin", "superadmin"].includes(m.sender_role);
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              {!mine && (
                <p className="text-[10px] font-semibold text-slate-500 mb-0.5 ml-1">
                  {m.sender_name}
                  {staff && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">{ROLE_LABEL[m.sender_role]}</span>}
                </p>
              )}
              {m.type === "file" ? <FileBubble m={m} mine={mine} /> : (
                <div className={`rounded-2xl px-3.5 py-2 max-w-[80%] ${mine ? "bg-teal-800 text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                  <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-0.5 mx-1">{timeStr(m.created_at)}</p>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-slate-100">
        <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" onChange={sendFile} data-testid="group-file-input" />
        <button type="button" data-testid="group-attach-btn" onClick={() => fileRef.current?.click()} disabled={busy}
          className="h-11 w-11 rounded-xl border border-slate-200 text-slate-500 hover:text-teal-700 hover:border-teal-300 flex items-center justify-center transition-colors disabled:opacity-40">
          <Paperclip size={17} />
        </button>
        <input data-testid="group-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Message the class…"
          className="flex-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" />
        <button data-testid="group-send-btn" type="submit" disabled={busy || !text.trim()}
          className="h-11 w-11 rounded-xl bg-teal-800 hover:bg-teal-700 text-white flex items-center justify-center disabled:opacity-40 transition-colors">
          <Send size={17} />
        </button>
      </form>
    </div>
  );
}

export function GroupChats() {
  const { pick } = useLang();
  const [rooms, setRooms] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/chat/rooms").then((r) => setRooms(r.data)).catch(() => setRooms([])); }, []);

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden grid lg:grid-cols-[320px,1fr]" style={{ minHeight: "560px", maxHeight: "72vh" }}>
      <div className={`border-r border-slate-100 flex-col overflow-y-auto ${selected ? "hidden lg:flex" : "flex"}`}>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-4 pt-4 pb-2">Course rooms</p>
        {!rooms ? <p className="text-sm text-slate-400 p-4">Loading…</p> : rooms.length === 0 ? (
          <p className="text-sm text-slate-400 p-5 text-center">Enroll in a course to join its class chat.</p>
        ) : rooms.map((r) => (
          <button key={r.track_id} data-testid={`chat-room-${r.track_id}`} onClick={() => setSelected(r.track_id)}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-50 hover:bg-teal-50/40 transition-colors ${selected === r.track_id ? "bg-teal-50/70" : ""}`}>
            <span className="w-9 h-9 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Users size={16} /></span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{pick(r.title)}</p>
              <p className="text-xs text-slate-400 truncate">{r.last_message ? `${r.last_sender}: ${r.last_message}` : "No messages yet"}</p>
            </div>
          </button>
        ))}
      </div>
      <div className={`${selected ? "flex" : "hidden lg:flex"} flex-col`}>
        {selected ? <Room trackId={selected} onBack={() => setSelected(null)} /> : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-300 gap-3 p-8">
            <Users size={44} strokeWidth={1.2} />
            <p className="text-sm text-slate-400">Pick a course room to chat with your whole class</p>
          </div>
        )}
      </div>
    </div>
  );
}
